import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../components/Button";
import Input from "../components/Input";
import Card from "../components/Card";
import {
  UserIcon,
  MailIcon,
  LockIcon,
  PhoneIcon,
  EyeIcon,
  EyeOffIcon,
  UserPlusIcon,
} from "../components/Icons";
import { authAPI } from "../services/api";

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    referralCode: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const referralFromUrl =
      searchParams.get("ref") ||
      searchParams.get("referral") ||
      searchParams.get("referralCode");

    if (!referralFromUrl) return;

    setFormData((prev) => ({
      ...prev,
      // Only prefill from URL when user has not entered a value yet.
      referralCode: prev.referralCode || referralFromUrl.trim(),
    }));
  }, [location.search]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!String(formData.referralCode || "").trim()) {
      setError("Referral code is required");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.register({
        email: formData.email,
        password: formData.password,
        name: formData.fullName,
        phone: formData.phone,
        referralCode: formData.referralCode,
      });

      if (response.success) {
        // Navigate to OTP verification with email
        navigate("/otp-verification", {
          state: {
            email: formData.email,
            type: "registration",
            userNumber: response.userNumber,
          },
        });
      }
    } catch (err) {
      setError(err.error || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[var(--bg-main)] bg-cover bg-center bg-no-repeat flex items-center justify-center px-4 sm:px-6 md:px-8 py-8"
      style={{ backgroundImage: "url('/auth-background.png')" }}
    >
      <Card className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-28 h-28 mx-auto mb-4 card-gradient rounded-full flex items-center justify-center">
            <img
              src="/logo-icon.png"
              alt="Aiarbix Logo"
              className="h-20 w-20 object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-[var(--text-primary)] mb-2">
            Create Account
          </h1>
          <p className="text-[var(--text-primary)]">
            Join us and start trading
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg">
            <p className="text-red-500 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleRegister} className="space-y-5">
          <Input
            type="text"
            name="fullName"
            label="Full Name"
            placeholder="Enter your full name"
            value={formData.fullName}
            onChange={handleChange}
            icon={<UserIcon className="w-5 h-5" />}
            required
          />

          <Input
            type="email"
            name="email"
            label="Email Address"
            placeholder="Enter your email"
            value={formData.email}
            onChange={handleChange}
            icon={<MailIcon className="w-5 h-5" />}
            required
          />

          <Input
            type="tel"
            name="phone"
            label="Phone Number"
            placeholder="Enter your phone number"
            value={formData.phone}
            onChange={handleChange}
            icon={<PhoneIcon className="w-5 h-5" />}
          />

          <Input
            type="text"
            name="referralCode"
            label="Referral Code"
            placeholder="Enter your referral code"
            value={formData.referralCode}
            onChange={handleChange}
            icon={<UserPlusIcon className="w-5 h-5" />}
            required
          />

          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              name="password"
              label="Password"
              placeholder="Create a password"
              value={formData.password}
              onChange={handleChange}
              icon={<LockIcon className="w-5 h-5" />}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-[42px] text-primary hover:text-primary/80 transition-colors"
            >
              {showPassword ? (
                <EyeOffIcon className="w-5 h-5" />
              ) : (
                <EyeIcon className="w-5 h-5" />
              )}
            </button>
          </div>

          <div className="relative">
            <Input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              label="Confirm Password"
              placeholder="Re-enter your password"
              value={formData.confirmPassword}
              onChange={handleChange}
              icon={<LockIcon className="w-5 h-5" />}
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-4 top-[42px] text-primary hover:text-primary/80 transition-colors"
            >
              {showConfirmPassword ? (
                <EyeOffIcon className="w-5 h-5" />
              ) : (
                <EyeIcon className="w-5 h-5" />
              )}
            </button>
          </div>

          <label className="flex items-start text-sm text-[var(--text-primary)] cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 mr-2 accent-primary"
              required
            />
            <span>
              I agree to the{" "}
              <span className="text-[var(--text-primary)]">
                Terms of Service
              </span>{" "}
              and{" "}
              <span className="text-[var(--text-primary)]">Privacy Policy</span>
            </span>
          </label>

          <Button type="submit" fullWidth disabled={loading}>
            {loading ? "Creating Account..." : "Create Account"}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm text-[var(--text-primary)]">
          Already have an account?{" "}
          <button
            onClick={() => navigate("/login")}
            className="text-[var(--text-primary)] hover:underline font-semibold"
          >
            Sign In
          </button>
        </div>
      </Card>
    </div>
  );
};

export default Register;
